import type { AuctionTrade, AuctionResourceType } from '@/pages/auction/AuctionPage';
import { AUCTION_RESOURCES } from '@/pages/auction/AuctionPage';
import './AuctionTradeHistory.css';

interface AuctionTradeHistoryProps {
  trades: AuctionTrade[];
  currentResource: AuctionResourceType;
}

export default function AuctionTradeHistory({
  trades,
  currentResource,
}: AuctionTradeHistoryProps) {
  // Filter trades for current resource and sort by most recent
  const filteredTrades = trades
    .filter(t => t.resource === currentResource)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  
  // Get all trades for stats
  const allTrades = trades.filter(t => t.resource === currentResource);
  
  // Calculate stats
  const totalVolume = allTrades.reduce((sum, t) => sum + t.quantity, 0);
  const avgPrice = allTrades.length > 0 
    ? Math.round(allTrades.reduce((sum, t) => sum + t.price, 0) / allTrades.length)
    : 0;
  const highPrice = allTrades.length > 0 
    ? Math.max(...allTrades.map(t => t.price))
    : 0;
  const lowPrice = allTrades.length > 0 
    ? Math.min(...allTrades.map(t => t.price))
    : 0;
  
  const resourceConfig = AUCTION_RESOURCES.find(r => r.id === currentResource);
  
  // Format timestamp
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: false 
    });
  };
  
  return (
    <div className="auction-trade-history">
      <h3 className="panel-title">
        {resourceConfig?.emoji} TRADE HISTORY
      </h3>
      
      {/* Stats */}
      <div className="trade-stats">
        <div className="stat-item">
          <span className="stat-label">Volume</span>
          <span className="stat-value">{totalVolume}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Avg</span>
          <span className="stat-value">${avgPrice}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">High</span>
          <span className="stat-value high">${highPrice}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Low</span>
          <span className="stat-value low">${lowPrice}</span>
        </div>
      </div>
      
      {/* Trade List */}
      <div className="trade-list">
        {filteredTrades.length === 0 ? (
          <div className="no-trades">
            <span className="no-trades-icon">📊</span>
            <span>No trades yet</span>
            <span className="no-trades-hint">Click in the arena to set your price</span>
          </div>
        ) : (
          filteredTrades.map((trade, index) => (
            <div 
              key={`${trade.id}_${index}`}
              className={`trade-item ${trade.isStoreTransaction ? 'store-trade' : ''}`}
            >
              <div className="trade-header">
                <span className="trade-time">{formatTime(trade.timestamp)}</span>
                <span className="trade-price">${trade.price}</span>
              </div>
              
              <div className="trade-details">
                <span className="trade-quantity">{trade.quantity}x</span>
                <span className="trade-arrow">
                  {trade.isStoreTransaction ? '🏪' : '↔️'}
                </span>
                <div className="trade-parties">
                  <span className="trade-buyer" title={trade.buyerName}>
                    📥 {trade.buyerName.substring(0, 8)}
                  </span>
                  <span className="trade-seller" title={trade.sellerName}>
                    📤 {trade.sellerName.substring(0, 8)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      
      {/* Market Info */}
      <div className="market-info">
        <h4>Market Info</h4>
        <div className="market-item">
          <span className="market-label">Base Price</span>
          <span className="market-value">${resourceConfig?.basePrice || 0}</span>
        </div>
        <div className="market-item">
          <span className="market-label">Price Range</span>
          <span className="market-value">
            ${resourceConfig?.minPrice || 0} - ${resourceConfig?.maxPrice || 0}
          </span>
        </div>
        {resourceConfig?.spoilage && resourceConfig.spoilage > 0 && (
          <div className="market-item spoilage">
            <span className="market-label">⚠️ Spoilage</span>
            <span className="market-value">
              {Math.round(resourceConfig.spoilage * 100)}% per turn
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

